import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import { LookupField, SkuTextField } from "../components/Products/TypedFields.jsx";
import {
    catalogDangerButtonSx,
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../components/Products/catalogGridStyles.js";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";
import PutData from "../services/PutData.js";
import PostPrintLabel from "../services/PostPrintLabel.js";
import { openPdfInNewTab } from "../services/openPrintTab.js";
import api from "../services/axiosInterceptor.js";
import SnackbarEx from "../components/Helpers/SnackbarEx.jsx";
import GridRowContextMenu, {
    buildShelfConfigShareUrl,
    copyShareUrlAction,
    useGridRowContextMenu,
    useSyncedUrlSearchQuery,
} from "../components/Helpers/GridRowContextMenu.jsx";
import ConfirmDeleteDialog from "../components/Helpers/ConfirmDeleteDialog.jsx";
import GridFilterBar, { useGridFilter } from "../components/Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../components/Helpers/GridLayoutControls.jsx";
import useGridLayout from "../components/Helpers/useGridLayout.js";
import useServerGridSort from "../components/Helpers/useServerGridSort.js";
import { GRID_LAYOUT_KEYS } from "../components/Helpers/gridLayoutKeys.js";
import { SHELF_FILTER_FIELDS } from "../components/Helpers/gridFilterFields.js";

const ENDPOINT = "broylesllc/shelf-configs";
const MIN_SEARCH = 2;

function itemIdLabel(value) {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") return value.text || value.name || "";
    return String(value);
}

function normalizeListPlatform(value) {
    if (Array.isArray(value)) return value.map((entry) => String(entry || "").trim()).filter(Boolean);
    if (typeof value === "string" && value.trim()) return [value.trim()];
    return [];
}

function catalogOption(record) {
    if (!record?.id) return null;
    return {
        id: record.id,
        itemId: itemIdLabel(record.itemId) || record.id,
        skuName: record.skuName || "",
        status: record.status || "",
        listPlatform: normalizeListPlatform(record.listPlatform),
        photo: record.photo || null,
    };
}

function itemMetaLine(item) {
    const parts = [];
    if (item?.status) parts.push(item.status);
    const platforms = normalizeListPlatform(item?.listPlatform);
    if (platforms.length) parts.push(platforms.join(", "));
    return parts.join(" · ");
}

function newBinRow(name = "", id = null) {
    return {
        key: id || `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id,
        name,
    };
}

function binsFromShelf(shelf) {
    return (shelf?.bins || []).map((bin) => newBinRow(bin.name || "", bin.id || null));
}

export default function ShelfConfigs() {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const { searchInput, setSearchInput, searchQ } = useSyncedUrlSearchQuery();
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [shelfName, setShelfName] = useState("");
    const [bins, setBins] = useState([]);
    const [linkedItems, setLinkedItems] = useState([]);
    const [itemSearch, setItemSearch] = useState("");
    const [debouncedItemSearch, setDebouncedItemSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const {
        menu: rowMenu,
        close: closeRowMenu,
        rowContextMenuSlotProps,
    } = useGridRowContextMenu();
    const { apply: applyFilter, filterActive, barProps: filterBar } =
        useGridFilter(SHELF_FILTER_FIELDS);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.shelfConfigs, {
        sortModel: [{ field: "created", sort: "desc" }],
        columnVisibilityModel: {},
    });
    const { sortModel, sortForApi, onSortModelChange } = useServerGridSort(
        layout,
        "created",
        "desc"
    );

    const selectedIds = useMemo(() => [...(selectionModel?.ids ?? [])], [selectionModel]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [searchQ]);

    const rowMenuActions = useMemo(
        () => [
            copyShareUrlAction({
                buildUrl: (row) => buildShelfConfigShareUrl(row),
                disabled: (row) => !String(row?.shelfName || row?.name || "").trim(),
                onCopied: () =>
                    setSnack({
                        open: true,
                        color: "success",
                        message: "Link copied.",
                    }),
            }),
        ],
        []
    );

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedItemSearch(itemSearch.trim()), 300);
        return () => clearTimeout(timer);
    }, [itemSearch]);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(filterActive ? 1 : paginationModel.page + 1),
            pageSize: String(filterActive ? 500 : paginationModel.pageSize),
            sort: sortForApi.sort,
            sortDir: sortForApi.sortDir,
        });
        if (searchQ) params.set("q", searchQ);
        return `api/${ENDPOINT}?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchQ,
        filterActive,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, load, refetch } = useApiData(listUrl);

    const detailUrl = editingId ? `api/${ENDPOINT}/${editingId}` : `api/${ENDPOINT}/none`;
    const { data: detail, load: detailLoading, error: detailError } = useApiData(detailUrl, {
        enabled: Boolean(dialogOpen && editingId),
    });

    const catalogUrl = `api/broylesllc/shelf-inventory/item-id-catalog?search=${encodeURIComponent(debouncedItemSearch)}&limit=100`;
    const { data: catalogData, load: catalogLoading } = useApiData(catalogUrl, {
        enabled: dialogOpen && debouncedItemSearch.length >= MIN_SEARCH,
    });

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
        setRows(items);
    }, [data]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);
    const gridSlotProps = useMemo(
        () => rowContextMenuSlotProps(filteredRows, appDataGridSlotProps),
        [rowContextMenuSlotProps, filteredRows]
    );

    useEffect(() => {
        if (filterActive) {
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }
    }, [filterActive]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [sortForApi.sort, sortForApi.sortDir]);

    const catalogOptions = useMemo(() => {
        const byId = new Map(linkedItems.map((item) => [item.id, item]));
        for (const record of catalogData || []) {
            const option = catalogOption(record);
            if (option && !byId.has(option.id)) byId.set(option.id, option);
        }
        return [...byId.values()];
    }, [catalogData, linkedItems]);

    // Scanner / paste UX: if search yields exactly one exact Item ID match, select it.
    const lastAutoSelectRef = useRef("");
    useEffect(() => {
        if (!dialogOpen || catalogLoading) return;
        const q = debouncedItemSearch.trim();
        if (q.length < MIN_SEARCH) return;
        const results = (Array.isArray(catalogData) ? catalogData : [])
            .map((record) => catalogOption(record))
            .filter(Boolean);
        if (results.length !== 1) return;
        const match = results[0];
        const needle = q.toLowerCase();
        const hay = String(match.itemId || "").trim().toLowerCase();
        if (hay !== needle) return;
        const key = `${needle}::${match.id}`;
        if (lastAutoSelectRef.current === key) return;
        lastAutoSelectRef.current = key;
        setLinkedItems((prev) =>
            prev.some((item) => item.id === match.id) ? prev : [...prev, match]
        );
        setItemSearch("");
    }, [dialogOpen, catalogLoading, debouncedItemSearch, catalogData]);

    const editingRow = rows.find((row) => row.id === editingId);
    const detailMatches = detail?.id === editingId ? detail : null;
    const appliedDetailId = useRef(null);

    // Apply detail when the dialog opens. Must depend on dialogOpen: reopening the
    // same shelf keeps editingId + cached detail unchanged, so a detail-only effect
    // would not re-run after openEdit clears linkedItems — items looked empty.
    useEffect(() => {
        if (!dialogOpen) {
            appliedDetailId.current = null;
            lastAutoSelectRef.current = "";
            return;
        }
        if (!editingId || !detailMatches) return;
        if (appliedDetailId.current === editingId) return;
        appliedDetailId.current = editingId;
        setLinkedItems(
            (detailMatches.itemIdCatalog || []).map((item) => catalogOption(item)).filter(Boolean)
        );
        setBins(binsFromShelf(detailMatches));
    }, [dialogOpen, detailMatches, editingId]);

    const baseColumns = useMemo(
        () => [
            { field: "name", headerName: "Name", flex: 1, minWidth: 160 },
            { field: "shelfName", headerName: "Shelf Name", flex: 1, minWidth: 140 },
            {
                field: "binNames",
                headerName: "Bins",
                flex: 1,
                minWidth: 160,
                sortable: false,
                valueGetter: (_value, row) => row.binNames || "",
            },
            {
                field: "binCount",
                headerName: "Bin count",
                width: 110,
                type: "number",
                sortable: false,
            },
            { field: "itemCount", headerName: "Items", width: 100, type: "number" },
            { field: "created", headerName: "Created", width: 120 },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.draftWidths, layout.applyWidths]
    );

    const openCreate = () => {
        setEditingId(null);
        setShelfName("");
        setBins([]);
        setLinkedItems([]);
        setItemSearch("");
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === id);
        if (!row) return;
        setEditingId(id);
        setShelfName(row.shelfName || "");
        setBins(binsFromShelf(row));
        setLinkedItems([]);
        setItemSearch("");
        setDialogOpen(true);
    };

    const updateBinName = (key, name) => {
        setBins((prev) => prev.map((bin) => (bin.key === key ? { ...bin, name } : bin)));
    };

    const removeBin = (key) => {
        setBins((prev) => prev.filter((bin) => bin.key !== key));
    };

    const addBin = () => {
        setBins((prev) => [...prev, newBinRow()]);
    };

    const save = async () => {
        const name = shelfName.trim();
        if (!name) {
            setSnack({ open: true, color: "error", message: "Shelf Name is required." });
            return;
        }
        const namedBins = bins
            .map((bin) => ({
                ...(bin.id ? { id: bin.id } : {}),
                name: String(bin.name || "").trim(),
            }))
            .filter((bin) => bin.name);
        const names = namedBins.map((bin) => bin.name.toLowerCase());
        if (new Set(names).size !== names.length) {
            setSnack({ open: true, color: "error", message: "Bin names must be unique on a shelf." });
            return;
        }
        setSaving(true);
        const payload = {
            shelfName: name,
            bins: namedBins,
            itemIdCatalog: linkedItems.map((item) => item.id),
        };
        const result = editingId
            ? await PutData(payload, `${ENDPOINT}/${editingId}`)
            : await PostData(payload, ENDPOINT);
        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Shelf updated." : "Shelf created.",
            });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } else {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Save failed.",
            });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id || row._id)))
            .map((row) => ({
                key: String(row.id || row._id),
                label: row.shelfName || String(row.id || row._id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`api/${ENDPOINT}/${id}`)));
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    const printSelectedBarcodes = async () => {
        const idSet = new Set(selectedIds.map(String));
        const payload = rows
            .filter((row) => idSet.has(String(row.id || row._id)))
            .map((row) => ({
                shelfName: String(row.shelfName || "").trim(),
            }))
            .filter((row) => row.shelfName);
        if (!payload.length) {
            setSnack({
                open: true,
                color: "warning",
                message: "A Shelf Name is required to print a barcode.",
            });
            return;
        }
        setPrinting(true);
        try {
            const { data: pdf, error: printError } = await PostPrintLabel(
                payload,
                "broylesllc/shelf-configs/print-labels"
            );
            if (pdf) {
                if (!openPdfInNewTab(pdf)) {
                    setSnack({
                        open: true,
                        color: "warning",
                        message: "Allow popups for this site to print barcodes.",
                    });
                    return;
                }
                setSelectionModel({ type: "include", ids: new Set() });
            } else {
                setSnack({
                    open: true,
                    color: "error",
                    message: printError?.message || "Error printing barcode.",
                });
            }
        } finally {
            setPrinting(false);
        }
    };

    return (
        <CatalogPageShell
            title="Shelf Configs"
            subtitle="Open a shelf to see the catalog items on it."
        >
            {error ? (
                <Box sx={{ px: 2, py: 1, color: "#b91c1c", fontSize: 13 }}>Failed to load shelves.</Box>
            ) : null}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search shelves…"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">{selectedIds.length} selected</span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || printing}
                        startIcon={
                            printing ? (
                                <CircularProgress size={14} />
                            ) : (
                                <LocalPrintshopOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={printSelectedBarcodes}
                        sx={catalogQuietButtonSx}
                    >
                        Print Barcode
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load)}
                        rows={filteredRows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortingMode={filterActive ? "client" : "server"}
                        sortModel={sortModel}
                        onSortModelChange={onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowClick={(params, event) => {
                            const target = event?.target;
                            if (target?.closest?.("input, .MuiCheckbox-root, button")) return;
                            openEdit(params.id);
                        }}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode={filterActive ? "client" : "server"}
                        rowCount={filterActive ? filteredRows.length : rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={gridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <GridRowContextMenu
                menu={rowMenu}
                onClose={closeRowMenu}
                actions={rowMenuActions}
            />

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? "Edit Shelf" : "Add Shelf"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        {editingId ? (
                            <LookupField label="Name" value={detailMatches?.name || editingRow?.name} source="Shelf name and created date" />
                        ) : null}
                        <SkuTextField
                            label="Shelf Name"
                            required
                            value={shelfName}
                            onChange={(event) => setShelfName(event.target.value)}
                        />
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#5c6570" }}>
                                    Bins
                                </Box>
                                <Button
                                    size="small"
                                    startIcon={<AddOutlinedIcon fontSize="small" />}
                                    onClick={addBin}
                                    sx={catalogQuietButtonSx}
                                >
                                    Add bin
                                </Button>
                            </div>
                            {bins.length === 0 ? (
                                <div className="text-muted small">
                                    No bins yet. Add one or more named bins for this shelf.
                                </div>
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {bins.map((bin, index) => (
                                        <Box
                                            key={bin.key}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label={`Bin ${index + 1}`}
                                                placeholder="Bin name"
                                                value={bin.name}
                                                onChange={(event) =>
                                                    updateBinName(bin.key, event.target.value)
                                                }
                                            />
                                            <Button
                                                size="small"
                                                aria-label={`Remove bin ${index + 1}`}
                                                onClick={() => removeBin(bin.key)}
                                                sx={{ minWidth: 36, px: 0.5, color: "#64748b" }}
                                            >
                                                <CloseOutlinedIcon fontSize="small" />
                                            </Button>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: "#5c6570" }}>
                                Item ID Catalog
                            </Box>
                            <Autocomplete
                                multiple
                                size="small"
                                options={catalogOptions}
                                value={linkedItems}
                                inputValue={itemSearch}
                                loading={catalogLoading}
                                filterOptions={(options) => options}
                                getOptionLabel={(option) => option?.itemId || option?.id || ""}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_event, next) => setLinkedItems(next)}
                                onInputChange={(_event, value, reason) => {
                                    if (reason === "input" || reason === "clear") setItemSearch(value);
                                }}
                                noOptionsText={
                                    debouncedItemSearch.length < MIN_SEARCH
                                        ? `Type at least ${MIN_SEARCH} characters to search`
                                        : "No item IDs found"
                                }
                                renderOption={(props, option) => {
                                    const { key, ...rest } = props;
                                    const meta = itemMetaLine(option);
                                    return (
                                        <li key={key} {...rest}>
                                            <div className="min-w-0">
                                                <div className="text-truncate">{option?.itemId || option?.id || ""}</div>
                                                {option?.skuName ? (
                                                    <div className="text-muted small text-truncate">{option.skuName}</div>
                                                ) : null}
                                                {meta ? (
                                                    <div className="text-muted small text-truncate">{meta}</div>
                                                ) : null}
                                            </div>
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        hiddenLabel
                                        placeholder="Search item IDs"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {catalogLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                        {editingId ? (
                            <>
                                <LookupField label="Created" value={detailMatches?.created || editingRow?.created} />
                                <div className="d-flex align-items-center justify-content-between">
                                    <span className="fw-semibold" style={{ fontSize: 12, color: "#5c6570" }}>
                                        Catalog items
                                    </span>
                                    {detailLoading ? <CircularProgress size={14} /> : null}
                                </div>
                                {detailError ? (
                                    <div className="text-danger small">Unable to load catalog items for this shelf.</div>
                                ) : null}
                                {detailMatches?.unresolvedCount ? (
                                    <div className="text-muted small">
                                        {detailMatches.unresolvedCount} catalog item
                                        {detailMatches.unresolvedCount === 1 ? "" : "s"} on this shelf
                                        {detailMatches.unresolvedCount === 1 ? " is" : " are"} not in the catalog database yet.
                                    </div>
                                ) : null}
                                {!detailLoading && linkedItems.length === 0 && !detailMatches?.unresolvedCount ? (
                                    <div className="text-muted small">No catalog items on this shelf.</div>
                                ) : null}
                            </>
                        ) : null}
                        {linkedItems.length ? (
                            <div className="list-group">
                                {linkedItems.map((item) => {
                                    const meta = itemMetaLine(item);
                                    return (
                                        <div key={item.id} className="list-group-item d-flex align-items-center gap-2 px-2 py-2">
                                            {item.photo?.url ? (
                                                <img
                                                    src={item.photo.url}
                                                    alt=""
                                                    width={40}
                                                    height={40}
                                                    style={{ objectFit: "cover", borderRadius: 6 }}
                                                />
                                            ) : (
                                                <div
                                                    className="bg-light border rounded"
                                                    style={{ width: 40, height: 40, flexShrink: 0 }}
                                                />
                                            )}
                                            <div className="min-w-0">
                                                <div className="fw-semibold text-truncate">{item.itemId}</div>
                                                {item.skuName ? (
                                                    <div className="text-muted small text-truncate">{item.skuName}</div>
                                                ) : null}
                                                {meta ? (
                                                    <div className="text-muted small text-truncate">{meta}</div>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || (Boolean(editingId) && detailLoading)}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? "Delete shelf?"
                        : `Delete ${deleteTargets.length} shelves?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </CatalogPageShell>
    );
}
